/**
 * The iManage document table: dense one-line rows (name with its kind, version,
 * author, modified, size), sortable columns, optional groups (Folders /
 * Documents), and keyboard movement.
 *
 * Keys: ↑/↓ or j/k move · Enter opens · Home/End jump · s toggles the star.
 * Columns drop out as the table narrows (container queries), so the preview
 * pane can open beside it without the rows wrapping.
 *
 * @module components/apps/imanage/IManageDocumentTable
 */
import type { IManageEntry } from "@amoreg/contracts/imanage";
import { ArrowDownIcon, ArrowUpIcon, ChevronRightIcon, StarIcon } from "lucide-react";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Skeleton } from "../../ui/skeleton";
import {
  defaultDirection,
  formatBytes,
  formatDateTime,
  formatWhen,
  recipientNames,
  rowAuthor,
  rowTime,
  type IManageSortKey,
  type SortDirection,
} from "./imanageFormat";
import { documentKey } from "./imanagePaths";
import { FileGlyph, fileKindLabel } from "./IManageParts";

export interface IManageTableGroup {
  readonly key: string;
  /** Shown as a quiet group row; omit for an ungrouped table. */
  readonly label?: string;
  readonly rows: readonly IManageEntry[];
  /** A note under the group (e.g. "Showing the first 50"). */
  readonly footer?: ReactNode;
}

export interface IManageSort {
  readonly key: IManageSortKey;
  readonly dir: SortDirection;
}

const COLUMNS: ReadonlyArray<{
  key: IManageSortKey;
  label: string;
  className: string;
  align?: "end";
}> = [
  { key: "name", label: "Name", className: "" },
  { key: "version", label: "Ver.", className: "hidden @lg:flex" },
  { key: "author", label: "Author", className: "hidden @3xl:flex" },
  { key: "modified", label: "Modified", className: "" },
  { key: "size", label: "Size", className: "hidden @2xl:flex", align: "end" },
];

/**
 * One grid for the header and every row, so columns line up. Cells hidden at a
 * width take no track (display: none), so the template lists only the shown
 * columns at each container width.
 */
const gridClass = (favorites: boolean) =>
  favorites
    ? "grid grid-cols-[minmax(0,1fr)_6rem_2.25rem] @lg:grid-cols-[minmax(0,1fr)_3.5rem_6rem_2.25rem] @2xl:grid-cols-[minmax(0,1fr)_3.5rem_6rem_4.5rem_2.25rem] @3xl:grid-cols-[minmax(0,1fr)_3.5rem_10rem_6rem_4.5rem_2.25rem]"
    : "grid grid-cols-[minmax(0,1fr)_6rem] @lg:grid-cols-[minmax(0,1fr)_3.5rem_6rem] @2xl:grid-cols-[minmax(0,1fr)_3.5rem_6rem_4.5rem] @3xl:grid-cols-[minmax(0,1fr)_3.5rem_10rem_6rem_4.5rem]";

export function IManageDocumentTable({
  groups,
  sort,
  onSortChange,
  selectedId,
  onOpen,
  loading = false,
  empty,
  isFavorite,
  onToggleFavorite,
  locate,
  label,
}: {
  readonly groups: readonly IManageTableGroup[];
  readonly sort: IManageSort;
  readonly onSortChange: (sort: IManageSort) => void;
  readonly selectedId: string | null;
  readonly onOpen: (entry: IManageEntry) => void;
  readonly loading?: boolean;
  readonly empty?: ReactNode;
  readonly isFavorite?: (entry: IManageEntry) => boolean;
  readonly onToggleFavorite?: (entry: IManageEntry) => void;
  /** Where a row lives, for lists that span workspaces ("1042 Whitestar Fund"). */
  readonly locate?: (entry: IManageEntry) => string | null;
  readonly label: string;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const rows = groups.flatMap((group) => group.rows);
  const selectedKey = selectedId ? documentKey(selectedId) : null;
  const favorites = onToggleFavorite !== undefined;
  const grid = gridClass(favorites);
  const focusIndex = Math.max(
    0,
    rows.findIndex((row) => row.kind === "document" && documentKey(row.id) === selectedKey),
  );

  // Keep the open document's row in view when it changes from outside (Back, a link).
  useEffect(() => {
    if (!selectedKey) return;
    bodyRef.current
      ?.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(selectedKey)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedKey]);

  const move = (event: KeyboardEvent<HTMLDivElement>, entry: IManageEntry) => {
    if (event.target !== event.currentTarget) return;
    const all = [...(bodyRef.current?.querySelectorAll<HTMLElement>("[data-imanage-row]") ?? [])];
    const index = all.indexOf(event.currentTarget);
    const go = (next: number) => {
      event.preventDefault();
      const target = all[Math.max(0, Math.min(all.length - 1, next))];
      target?.focus();
      target?.scrollIntoView({ block: "nearest" });
    };
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key === "ArrowDown" || event.key === "j") go(index + 1);
    else if (event.key === "ArrowUp" || event.key === "k") go(index - 1);
    else if (event.key === "Home") go(0);
    else if (event.key === "End") go(all.length - 1);
    else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(entry);
    } else if (event.key === "s" && onToggleFavorite) {
      event.preventDefault();
      onToggleFavorite(entry);
    }
  };

  const header = (
    <div role="rowgroup" className="sticky top-0 z-10 bg-background">
      <div
        role="row"
        className={cn(grid, "h-8 items-center border-b border-border/60 text-xs text-muted-foreground")}
      >
        {COLUMNS.map((column) => {
          const active = sort.key === column.key;
          return (
            <div
              key={column.key}
              role="columnheader"
              aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
              className={cn(
                "flex items-center px-2 first:pl-5",
                column.className,
                column.align === "end" && "justify-end",
                column.align === "end" && !favorites && "pr-5",
              )}
            >
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1 rounded-sm outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
                  active && "text-foreground",
                )}
                onClick={() =>
                  onSortChange({
                    key: column.key,
                    dir: active ? (sort.dir === "asc" ? "desc" : "asc") : defaultDirection(column.key),
                  })
                }
              >
                {column.label}
                {active ? (
                  sort.dir === "asc" ? (
                    <ArrowUpIcon aria-hidden className="size-3" />
                  ) : (
                    <ArrowDownIcon aria-hidden className="size-3" />
                  )
                ) : null}
              </button>
            </div>
          );
        })}
        {favorites ? (
          <div role="columnheader">
            <span className="sr-only">Favorite</span>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (loading)
    return (
      <div className="@container min-w-0" aria-busy="true" aria-label={`Loading ${label}`}>
        <div role="table" aria-label={label} className="text-[13px]">
          {header}
        </div>
        <div className="flex flex-col">
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="flex h-9 items-center gap-3 border-b border-border/40 px-5">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-3.5" style={{ width: `${46 - ((i * 9) % 22)}%` }} />
              <Skeleton className="ms-auto h-3.5 w-14" />
            </div>
          ))}
        </div>
      </div>
    );

  let index = -1;
  return (
    <div className="@container min-w-0">
      <div role="grid" aria-label={label} className="text-[13px]">
        {rows.length > 0 ? header : null}
        <div role="rowgroup" ref={bodyRef}>
          {groups.map((group) =>
            group.rows.length === 0 ? null : (
              <div key={group.key} role="presentation">
                {group.label ? (
                  <div role="row" className="flex h-8 items-center bg-muted/40 px-5" data-imanage-group={group.key}>
                    <div role="rowheader" className="text-xs font-medium text-muted-foreground">
                      {group.label}
                      <span className="ms-1.5 font-normal tabular-nums text-muted-foreground/70">
                        {group.rows.length}
                      </span>
                    </div>
                  </div>
                ) : null}
                {group.rows.map((entry) => {
                  index++;
                  const key = entry.kind === "document" ? documentKey(entry.id) : entry.id;
                  const selected = entry.kind === "document" && key === selectedKey;
                  const starred = isFavorite?.(entry) ?? false;
                  const author = rowAuthor(entry);
                  const time = rowTime(entry);
                  return (
                    <div
                      key={entry.id}
                      role="row"
                      data-imanage-row=""
                      data-row-key={key}
                      data-selected={selected ? "" : undefined}
                      tabIndex={index === focusIndex ? 0 : -1}
                      aria-selected={selected}
                      onClick={() => onOpen(entry)}
                      onKeyDown={(event) => move(event, entry)}
                      className={cn(
                        grid,
                        "group/row h-9 cursor-default items-center border-b border-border/40 outline-none transition-colors",
                        "hover:bg-accent/50 focus-visible:bg-accent/70 focus-visible:shadow-[inset_2px_0_0_var(--color-ring)]",
                        selected && "bg-accent hover:bg-accent",
                      )}
                    >
                      <div role="gridcell" className="flex min-w-0 items-center gap-2.5 px-2 pl-5">
                        <FileGlyph entry={entry} />
                        <span className="min-w-0 truncate" title={entry.name}>
                          {entry.name}
                        </span>
                        {entry.email && entry.emailTo ? (
                          <span className="hidden min-w-0 shrink truncate text-muted-foreground @2xl:inline">
                            to {recipientNames(entry.emailTo)}
                          </span>
                        ) : null}
                        {locate ? (
                          <span className="ms-auto hidden max-w-[40%] shrink-0 truncate ps-2 text-xs text-muted-foreground @xl:inline">
                            {locate(entry)}
                          </span>
                        ) : null}
                        {entry.kind === "folder" ? (
                          <ChevronRightIcon
                            aria-hidden
                            className="ms-auto size-3.5 shrink-0 text-muted-foreground/60"
                          />
                        ) : null}
                        <span className="sr-only">, {fileKindLabel(entry)}</span>
                      </div>
                      <div
                        role="gridcell"
                        className="hidden px-2 font-mono text-xs text-muted-foreground tabular-nums @lg:block"
                      >
                        {entry.version !== null && !entry.email ? `v${entry.version}` : ""}
                      </div>
                      <div role="gridcell" className="hidden truncate px-2 text-muted-foreground @3xl:block">
                        {author ?? ""}
                      </div>
                      <div
                        role="gridcell"
                        className="truncate px-2 text-muted-foreground tabular-nums"
                        title={time ? formatDateTime(time) : undefined}
                      >
                        {time ? formatWhen(time) : ""}
                      </div>
                      <div
                        role="gridcell"
                        className={cn(
                          "hidden px-2 text-right text-muted-foreground tabular-nums @2xl:block",
                          !favorites && "pr-5",
                        )}
                      >
                        {entry.kind === "document" ? formatBytes(entry.size) : ""}
                      </div>
                      {onToggleFavorite ? (
                        <div role="gridcell" className="flex justify-center">
                          <button
                            type="button"
                            tabIndex={-1}
                            aria-label={
                              starred ? `Remove ${entry.name} from favorites` : `Add ${entry.name} to favorites`
                            }
                            aria-pressed={starred}
                            onClick={(event) => {
                              event.stopPropagation();
                              onToggleFavorite(entry);
                            }}
                            className={cn(
                              "flex size-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover/row:opacity-100 group-focus-visible/row:opacity-100",
                              starred && "text-warning opacity-100",
                            )}
                          >
                            <StarIcon aria-hidden className={cn("size-3.5", starred && "fill-current")} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {group.footer ? (
                  <div role="row" className="px-5 py-2 text-xs text-muted-foreground">
                    <div role="gridcell">{group.footer}</div>
                  </div>
                ) : null}
              </div>
            ),
          )}
        </div>
      </div>
      {rows.length === 0 && empty ? empty : null}
    </div>
  );
}
